import React, { useState, useEffect, useCallback } from 'react';
//import { useAuth } from '../../../context/AuthContext';
import { createConversation, getMessagesByConversationId, getConversations, saveMessage } from '../../../utils/messanger.controller';
import styles from './Chat.module.css';
import LoginButton from '../../Design/Button/Login/LoginButton';

function Chat({ user }) {
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Define loadConversationMessages first
  const loadConversationMessages = useCallback(async (conversationId) => {
    try {
      const conversationMessages = await getMessagesByConversationId(conversationId);
      setMessages(conversationMessages);
      console.log('Loaded messages:', conversationMessages);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Failed to load messages');
    }
  }, []);  // No need to include setMessages and setError as dependencies

  // Now use loadConversationMessages in fetchUserConversations
  const fetchUserConversations = useCallback(async () => {
    try {
      setIsLoading(true);
      const userConversations = await getConversations(user.user_id);
      setConversations(userConversations);
      
      if (userConversations.length > 0) {
        setActiveConversation(userConversations[0]);
        await loadConversationMessages(userConversations[0].id);
      }
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError('Failed to load conversations');
      setIsLoading(false);
    }
  }, [user, loadConversationMessages]);  // Include loadConversationMessages as dependency

  useEffect(() => {
    if (user) {
      fetchUserConversations();
    }
  }, [user, fetchUserConversations]);

  // Handle conversation selection
  const handleConversationSelect = (conversation) => {
    setActiveConversation(conversation);
    loadConversationMessages(conversation.id);
  };

  // Create a new conversation
  const handleCreateConversation = async () => {
    if (!subject.trim()) {
      setError('Please enter a subject for your conversation');
      return;
    }
    
    try {
      setError('');
      const newConversation = await createConversation({
        user_id: user.user_id,
        subject: subject.trim()
      });
      
      // Add the new conversation to the list and set it as active
      setConversations([newConversation[0], ...conversations]);
      setActiveConversation(newConversation[0]);
      setMessages([]);
      setSubject('');
    } catch (err) {
      console.error('Error creating conversation:', err);
      setError('Failed to create conversation');
    }
  };

  // Handle message submission
  const handleMessageSubmit = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !activeConversation) return;
    
    try {
      // Prepare message data
      const messageData = {
        conversation_id: activeConversation.id,
        user_id: user.user_id,
        message: newMessage.trim()
      };
      
      const sentMessage = await saveMessage(messageData);
      
      // Add new message to the display
      setMessages([...messages, sentMessage[0]]);
      setNewMessage('');
      setError('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  return (
    <div className={styles.chatContainer}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      
      <div className={styles.chatSidebar}>
        {/* Always show the new conversation form */}
        <div className={styles.newConversation}>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="New conversation subject..."
          />
          <LoginButton 
            type="button"
            onClick={handleCreateConversation}
            text="Start New"
          />
        </div>
        
        <div className={styles.conversationList}>
          {isLoading ? (
            <p>Loading conversations...</p>
          ) : conversations.length === 0 ? (
            <p>No conversations yet. Start a new one!</p>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`${styles.conversationItem} ${activeConversation?.id === conv.id ? styles.active : ''}`}
                onClick={() => handleConversationSelect(conv)}
              >
                <div className={styles.conversationTitle}>{conv.subject}</div>
                <div className={styles.conversationStatus}>{conv.status}</div>
                <div className={styles.conversationDate}>
                {new Date(conv.updated_at).toLocaleString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit',
                  month: '2-digit',
                  day: '2-digit',
                  year: 'numeric'
                })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
      <div className={styles.chatMain}>
        {!activeConversation ? (
          <div className={styles.noChatSelected}>
            <p>You don't have any active conversations. To get started, enter a subject on the left and click "Start New" to create your first conversation.</p>
          </div>
        ) : (
          <>
            <div className={styles.chatHeader}>
              <h3>{activeConversation.subject}</h3>
              <span className={styles.chatStatus}>{activeConversation.status}</span>
            </div>
            
            <div className={styles.messagesArea}>
              {messages.length === 0 ? (
                <p>No messages yet. Start the conversation!</p>
              ) : (
                messages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`${styles.messageItem} ${msg.sender_user_id === user.user_id ? styles.sent : styles.received}`}
                  >
                    <p>{msg.message_body}</p>
                    <span className={styles.timestamp}>
                      {new Date(msg.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                ))
              )}
            </div>
            
            <form className={styles.messageForm}>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
              />
              <LoginButton
                type="button"
                onClick={handleMessageSubmit}
                text="Send" 
                disabled={!newMessage.trim()}
              />
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default Chat;